import React from 'react';
import { useCustom } from '@refinedev/core';
import { Card, Space, Tag, theme } from 'antd';
import type { EntitlementsDto, FeatureKeyDto } from '@wellverse/shared-ts';
import { LIVE_REFRESH_MS } from './efx-membership';

const FEATURE_LABELS: Record<FeatureKeyDto, string> = {
  friends_connect: 'Connect with friends',
  workout_quick_log: 'Quick log',
  equipment_scan: 'Scan equipment',
  ai_workout_generator: 'AI workout generator',
  workout_plan_create: 'Create my workout plan',
  workout_idea_explore: 'Explore workout idea',
  ai_food_scan: 'AI scan food',
  ai_food_text_search: 'AI text search food',
  ai_report_insight: 'AI insight (Progress Report)',
  weight_tracking: 'Weight',
  body_fat_tracking: 'Body Fat %',
};

const TIER_COLORS = { FREE: 'default', EFX: 'blue', PREMIUM: 'gold' } as const;

/**
 * The member type the API will apply to this member right now — Premium, EFX
 * or Free — and what that means feature by feature, with today's use of the
 * limited ones. Read-only: the rules are set under Member Permissions.
 */
export const UserFeatureAccess: React.FC<{ userId: string }> = ({ userId }) => {
  const { token } = theme.useToken();
  const { data, isLoading } = useCustom<EntitlementsDto>({
    url: `/admin/entitlements/members/${userId}`,
    method: 'get',
    // Follows the EFX membership card: a lapsed membership changes the tier.
    queryOptions: { refetchInterval: LIVE_REFRESH_MS, refetchOnWindowFocus: true },
  });
  const entitlements = data?.data;

  return (
    <Card title="Feature Access" size="small" loading={isLoading} style={{ marginTop: 24 }}>
      {entitlements && (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space size={8}>
            <span>Member type</span>
            <Tag color={TIER_COLORS[entitlements.tier]}>{entitlements.tier}</Tag>
            <span style={{ color: token.colorTextSecondary }}>
              daily counts for {entitlements.localDate} (Hong Kong time here; the app counts in the
              member's own timezone)
            </span>
          </Space>
          <Space size={[8, 8]} wrap>
            {(Object.keys(FEATURE_LABELS) as FeatureKeyDto[]).map((key) => {
              const rule = entitlements.features[key];
              if (!rule) return null;
              const text =
                rule.access === 'LIMIT'
                  ? `${rule.usedToday ?? 0} / ${rule.dailyLimit} today`
                  : rule.access === 'ALLOW'
                    ? 'allowed'
                    : rule.access === 'READ_ONLY'
                      ? 'read only'
                      : 'not allowed';
              const blocked =
                rule.access === 'DENY' ||
                rule.access === 'READ_ONLY' ||
                (rule.access === 'LIMIT' && (rule.remainingToday ?? 0) === 0);
              return (
                <Tag key={key} color={blocked ? 'default' : 'green'}>
                  {FEATURE_LABELS[key]}: {text}
                </Tag>
              );
            })}
          </Space>
        </Space>
      )}
    </Card>
  );
};
